import { OnboardingGuideRoot } from "@src/backpack-console/components/OnboardingGuide/OnboardingGuideRoot";
import { useCurrentAccount } from "@src/backpack-console/CurrentAccountManager";
import Button from "@src/straps/base/buttons/Button/Button";
import Icon from "@src/straps/base/icons/Icon/Icon";
import UserHeader, {
  UserHeaderUser,
} from "@src/straps/base/navigation/UserHeader/UserHeader";
import classNames from "classnames";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { useRouteMatch } from "react-router-dom";
import LinkWithQuery from "@src/straps/base/navigation/LinkWithQuery/LinkWithQuery";
import Dropdown from "../../inputs/Dropdown/Dropdown";
import { SelectedSite } from "./BuildingHeader";
import { BuildingSearch } from "./BuildingSearch";

export const TOP_NAV_HEIGHT = 70;

function NavBorder() {
  return <div className="my-3 w-px shrink-0 bg-white/25" />;
}

const HomeButton = () => {
  return (
    <LinkWithQuery
      to="/"
      queries={["accountId"]}
      data-testid="top-nav-home-top-button"
      className="group flex h-[70px] w-[70px] cursor-pointer flex-col items-center justify-center bg-straps-brand px-0 text-white outline-none transition hover:bg-straps-brand-hover"
    >
      <Icon name="knockout" size="large" className="text-pure-white" />
    </LinkWithQuery>
  );
};

function AccountSwitcher() {
  const { account, accounts, setAccountId, isLoading } = useCurrentAccount();
  const showAccountSwitcherDropdown =
    accounts && accounts.length > 1 && setAccountId;

  if (!showAccountSwitcherDropdown || isLoading) {
    return null;
  }
  return (
    <div className="flex w-48 items-center">
      <Dropdown
        dataTestId="account-switcher-dropdown"
        variant="largeSearch"
        tint="brand"
        options={accounts}
        controlledValue={account}
        onSelect={(option) => {
          if (Array.isArray(option)) {
            return;
          }
          setAccountId(option?.id);
        }}
      />
    </div>
  );
}

interface TopNavBarProps {
  readonly selectedSite?: SelectedSite | null;
  readonly user?: UserHeaderUser | null;
}

const accountSwitcherHiddenAtom = atom<boolean>(false);
// Hides the account switcher and building select while rendered
export const HideTopNavAccountSwitcher = () => {
  const setHidden = useSetAtom(accountSwitcherHiddenAtom);

  useEffect(() => {
    setHidden(true);
    return () => setHidden(false);
  }, [setHidden]);

  return null;
};

const TopNavBar = ({ selectedSite, user }: TopNavBarProps) => {
  const isAccountSwitcherHidden = useAtomValue(accountSwitcherHiddenAtom);
  // Every site route shows an onboarding guide, so reserve its width here to keep
  // the nav from collapsing while the guide subtree remounts on a site change.
  const isSiteRoute = !!useRouteMatch({ path: "/site/:siteId" });

  return (
    <nav>
      <div className="relative z-100 flex border-b border-b-white/20 bg-straps-brand text-pure-white">
        {/* Icon Nav */}
        <HomeButton />
        <NavBorder />
        {!isAccountSwitcherHidden ? (
          <>
            <AccountSwitcher />
            {/* Search input */}
            <BuildingSearch selected={selectedSite} />
          </>
        ) : (
          <div className="grow" />
        )}
        <OnboardingGuideRoot
          // min-w must match ONBOARDING_GUIDE_WIDTH (the portalled guide's width)
          className={classNames(
            "shrink-0 transition-[min-width] duration-300",
            isSiteRoute ? "min-w-[242px]" : "min-w-0"
          )}
        />
        {isSiteRoute ? <NavBorder /> : null}
        {/* Support link */}
        <Button
          className="px-6"
          color="white"
          component="a"
          icon="view-drawing"
          iconRight
          noFrameVariant="hyperlink"
          href="https://kb.backpacknetworks.com/"
          target="_blank"
          dataTestId="support-center-link-button"
        >
          Support Center
        </Button>
        <NavBorder />
        {/* Settings tab */}
        <UserHeader user={user} settingsURL="/settings" />
      </div>
    </nav>
  );
};

export default TopNavBar;
